import { dueProperties } from '../notes/notes.utils';

export function dueDateWrites(
  current: { date?: string; datetime?: string },
  newDate: string | null
): Array<{
  propertyName: 'ahoy_date' | 'ahoy_datetime';
  value: string | null;
}> {
  const writes: Array<{
    propertyName: 'ahoy_date' | 'ahoy_datetime';
    value: string | null;
  }> = [];

  if (newDate) {
    const target = dueProperties(newDate);
    // For a datetime input, `dueProperties` OMITS `ahoy_date` (the backend
    // NoteDueDateCascade derives it server-side) — the key is absent, not null.
    // Only write `ahoy_date` when `dueProperties` actually produced one (a
    // date-only input); coercing the omission to `ahoy_date: null` would clear
    // the derived date. `'ahoy_date' in target` distinguishes "omitted"
    // (datetime input) from "explicitly null".
    if (
      'ahoy_date' in target &&
      (target.ahoy_date ?? null) !== (current.date ?? null)
    ) {
      writes.push({
        propertyName: 'ahoy_date',
        value: target.ahoy_date ?? null,
      });
    }
    // Write `ahoy_datetime` to the target's value — the datetime for a timed
    // input, `null` (clearing the stale time) for a date-only input — whenever
    // it actually changed.
    if ((target.ahoy_datetime ?? null) !== (current.datetime ?? null)) {
      writes.push({
        propertyName: 'ahoy_datetime',
        value: target.ahoy_datetime ?? null,
      });
    }
  } else {
    if ((current.datetime ?? null) !== null) {
      writes.push({ propertyName: 'ahoy_datetime', value: null });
    }
    if ((current.date ?? null) !== null) {
      writes.push({ propertyName: 'ahoy_date', value: null });
    }
  }

  return writes;
}

/**
 * Apply the due-date change for `newDate` as ONE atomic property write.
 *
 * `ahoy_date` and `ahoy_datetime` are companion fields for a single logical
 * due date, and they must never persist in an inconsistent pair (e.g. a new
 * `ahoy_date` alongside a stale `ahoy_datetime` that still wins in the
 * display). Submitting both changed properties in a single event makes that
 * impossible: the write either commits as a whole or rolls back as a whole —
 * there is no partial-failure window to compensate for, and no pre-edit
 * snapshot to restore over a concurrent update. `dueDateWrites` still narrows
 * the payload to only the properties that actually changed, so an unchanged
 * companion field is never submitted (the backend rejects a no-op write).
 *
 * When nothing changed the batch is empty and `submit` is not called at all —
 * the caller never issues a no-op mutation.
 */
export async function submitDueDateWrites(
  current: { date?: string; datetime?: string },
  newDate: string | null,
  submit: (properties: Record<string, string | null>) => Promise<void>
): Promise<void> {
  const writes = dueDateWrites(current, newDate);
  if (writes.length === 0) {
    return;
  }
  const properties: Record<string, string | null> = {};
  for (const write of writes) {
    properties[write.propertyName] = write.value;
  }
  await submit(properties);
}
